import { useCallback } from 'react';

import {
  RetailerNetworkCreateMutationOptions,
  RetailerNetworkDeleteMutationOptions,
  RetailerNetworkUpdateMutationOptions,
  RemoveLocationFromRetailerNetworkMutationOptions,
  RemoveUserFromRetailerNetworkMutationOptions,
  RetailerNetworkUserCreateMutationOptions,
  RetailerNetworkUserUpdateMutationOptions,
  RetailerNetworkLocationGroupCreateMutationOptions,
  RetailerNetworkLocationGroupUpdateMutationOptions,
  RetailerNetworkLocationGroupAddLocationsMutationOptions,
  RetailerNetworkLocationGroupRemoveLocationsMutationOptions,
  RetailerNetworkLocationGroupDeleteMutationOptions,
  DisassociateLocationsWithRetailerNetworkUsersMutationOptions,
  AssociateLocationsWithRetailerNetworkUsersMutationOptions,
  LocationBulkOperationHoursUpdateForRetailerNetworkMutationOptions,
  useLocationBulkOperationHoursUpdateForRetailerNetworkMutation,
  AssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutationOptions,
  SetLocationPerformanceGoalMutationOptions,
  UnsetLocationPerformanceGoalMutationOptions,
  useRetailerNetworkCreateMutation as useApolloRetailerNetworkCreateMutation,
  useRetailerNetworkDeleteMutation as useApolloRetailerNetworkDeleteMutation,
  useRetailerNetworkUpdateMutation as useApolloRetailerNetworkUpdateMutation,
  useRetailerNetworkLocationGroupCreateMutation as useApolloRetailerNetworkLocationGroupCreateMutation,
  useRetailerNetworkLocationGroupUpdateMutation as useApolloRetailerNetworkLocationGroupUpdateMutation,
  useRetailerNetworkLocationGroupDeleteMutation as useApolloRetailerNetworkLocationGroupDeleteMutation,
  useRetailerNetworkLocationGroupAddLocationsMutation as useApolloRetailerNetworkLocationGroupAddLocationsMutation,
  useRetailerNetworkLocationGroupRemoveLocationsMutation as useApolloRetailerNetworkLocationGroupRemoveLocationsMutation,
  useRemoveLocationFromRetailerNetworkMutation as useApolloRemoveLocationFromRetailerNetworkMutation,
  useRemoveUserFromRetailerNetworkMutation as useApolloRemoveUserFromRetailerNetworkMutation,
  useRetailerNetworkUserCreateMutation as useApolloRetailerNetworkUserCreateMutation,
  useRetailerNetworkUserUpdateMutation as useApolloRetailerNetworkUserUpdateMutation,
  useDisassociateLocationsWithRetailerNetworkUsersMutation as useApolloDisassociateLocationsWithRetailerNetworkUsersMutation,
  useAssociateLocationsWithRetailerNetworkUsersMutation as useApolloAssociateLocationsWithRetailerNetworkUsersMutation,
  useAssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation as useApolloAssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation,
  useSetLocationPerformanceGoalMutation as useApolloSetLocationPerformanceGoalMutation,
  useUnsetLocationPerformanceGoalMutation as useApolloUnsetLocationPerformanceGoalMutation,
} from '@occc/graphql';
import { useMutation } from '@occc-app/dataAccessLayer/utils';

export const useRetailerNetworkCreateMutation = (
  baseOptions: RetailerNetworkCreateMutationOptions,
) => useMutation(useApolloRetailerNetworkCreateMutation, baseOptions);

export const useRetailerNetworkUpdateMutation = (
  baseOptions?: RetailerNetworkUpdateMutationOptions,
) => useMutation(useApolloRetailerNetworkUpdateMutation, baseOptions);

export const useRetailerNetworkDeleteMutation = (
  baseOptions?: RetailerNetworkDeleteMutationOptions,
) => useMutation(useApolloRetailerNetworkDeleteMutation, baseOptions);

export const useRetailerNetworkLocationGroupCreateMutation = (
  baseOptions: RetailerNetworkLocationGroupCreateMutationOptions,
) => useMutation(useApolloRetailerNetworkLocationGroupCreateMutation, baseOptions);

export const useRetailerNetworkLocationGroupUpdateMutation = (
  baseOptions: RetailerNetworkLocationGroupUpdateMutationOptions,
) => useMutation(useApolloRetailerNetworkLocationGroupUpdateMutation, baseOptions);

export const useRetailerNetworkLocationGroupDeleteMutation = (
  baseOptions: RetailerNetworkLocationGroupDeleteMutationOptions,
) => useMutation(useApolloRetailerNetworkLocationGroupDeleteMutation, baseOptions);

export const useRetailerNetworkLocationGroupAddLocationsMutation = (
  baseOptions: RetailerNetworkLocationGroupAddLocationsMutationOptions,
) => useMutation(useApolloRetailerNetworkLocationGroupAddLocationsMutation, baseOptions);

export const useRetailerNetworkLocationGroupRemoveLocationsMutation = (
  baseOptions: RetailerNetworkLocationGroupRemoveLocationsMutationOptions,
) => useMutation(useApolloRetailerNetworkLocationGroupRemoveLocationsMutation, baseOptions);

export const useRemoveLocationFromRetailerNetworkMutation = (
  baseOptions?: RemoveLocationFromRetailerNetworkMutationOptions,
) => useMutation(useApolloRemoveLocationFromRetailerNetworkMutation, baseOptions);

export const useRemoveUserFromRetailerNetworkMutation = (
  baseOptions?: RemoveUserFromRetailerNetworkMutationOptions,
) => useMutation(useApolloRemoveUserFromRetailerNetworkMutation, baseOptions);

export const useRetailerNetworkUserCreateMutation = (
  baseOptions?: RetailerNetworkUserCreateMutationOptions,
) => {
  const [mutate, { client }] = useMutation(useApolloRetailerNetworkUserCreateMutation, baseOptions);

  const createRetailerNetworkUser = useCallback(
    async (params: RetailerNetworkUserCreateMutationOptions) => {
      const result = await mutate(params);

      // Refetch the retailer network users query to update the cache
      // RC user can be created even if the mutation fails. It's temporary solution while BE logic hasn't changed
      if (result.errors) {
        client.refetchQueries({ include: ['retailerNetworkUsers'] });
      }
    },
    [mutate, client],
  );

  return [createRetailerNetworkUser];
};

export const useRetailerNetworkUserUpdateMutation = (
  baseOptions?: RetailerNetworkUserUpdateMutationOptions,
) => useMutation(useApolloRetailerNetworkUserUpdateMutation, baseOptions);

export const useDisassociateLocationsWithRetailerNetworkUsersMutation = (
  baseOptions?: DisassociateLocationsWithRetailerNetworkUsersMutationOptions,
) => useMutation(useApolloDisassociateLocationsWithRetailerNetworkUsersMutation, baseOptions);

export const useAssociateLocationsWithRetailerNetworkUsersMutation = (
  baseOptions?: AssociateLocationsWithRetailerNetworkUsersMutationOptions,
) => useMutation(useApolloAssociateLocationsWithRetailerNetworkUsersMutation, baseOptions);

export const useAssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation = (
  baseOptions?: AssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutationOptions,
) =>
  useMutation(
    useApolloAssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation,
    baseOptions,
  );

export const useSetLocationPerformanceGoalMutation = (
  baseOptions?: SetLocationPerformanceGoalMutationOptions,
) => useMutation(useApolloSetLocationPerformanceGoalMutation, baseOptions);

export const useUnsetLocationPerformanceGoalMutation = (
  baseOptions?: UnsetLocationPerformanceGoalMutationOptions,
) => useMutation(useApolloUnsetLocationPerformanceGoalMutation, baseOptions);

export const useLocationBulkOperationHoursUpdateForRetailerNetwork = (
  baseOptions?: LocationBulkOperationHoursUpdateForRetailerNetworkMutationOptions,
) => useMutation(useLocationBulkOperationHoursUpdateForRetailerNetworkMutation, baseOptions);
